import { z } from 'zod';
/**
 * A failure a BoardFlight page reports about itself (`POST /v1/client/errors`), so an operator hears
 * of a broken page without anybody having to tell them.
 *
 * It names nobody: no account, no session, no install id, and the page's path without its query or
 * fragment, which is where codes and ids travel. It is mailed to operators and kept nowhere, so the
 * only thing worth knowing about it afterwards is that a page broke and where.
 */
/** How the page learnt of it: a thrown error, a promise nobody caught, or React giving up a render. */
export const ClientErrorKind = z.enum(['error', 'rejection', 'render']);
export const MAX_CLIENT_ERROR_MESSAGE = 500;
export const MAX_CLIENT_ERROR_STACK = 2000;
export const ClientErrorReport = z.object({
    kind: ClientErrorKind,
    message: z.string().max(MAX_CLIENT_ERROR_MESSAGE),
    stack: z.string().max(MAX_CLIENT_ERROR_STACK).nullish(),
    /** `location.pathname` and nothing after it. */
    path: z.string().regex(/^\/[^?#]*$/).max(200),
    /** The build the page was loaded from (`build-version.ts`), or null under `vite dev`. */
    build: z.string().trim().max(40).nullish(),
    /** Whether the page is in a browser tab or the desktop app's window. */
    surface: z.enum(['web', 'desktop']),
});
/**
 * Pages whose path carries a secret or a person: a reset link's token, an email check's, an
 * invitation's, a redeem code, a share code, a username.
 */
const NAMED_ROOTS = new Set(['reset', 'verify', 'invite', 'redeem', 'say', 'g', 'u']);
/**
 * A page's path as a report may carry it. Below one of `NAMED_ROOTS` everything after the first
 * segment is replaced; elsewhere, every later segment that is long or has a digit in it, which is
 * every id. What stays says which page it was; a slug of words survives.
 */
export function reportablePath(pathname) {
    const segments = (pathname.split(/[?#]/)[0] || '/').split('/');
    const named = NAMED_ROOTS.has(segments[1] ?? '');
    return (segments
        .map((segment, index) => index > 1 && segment !== '' && (named || segment.length > 24 || /\d/.test(segment)) ? ':x' : segment)
        .join('/')
        .slice(0, 200) || '/');
}
//# sourceMappingURL=client-errors.js.map